import gql from 'graphql-tag';
import { clients } from '../../../util/clients';
import { fieldsToGqlString, mergeByIdNewAtTop } from '../../../util/shared/vue-global';

const { backend, backendRest } = clients.direct;
const defaultFields = {
  _id: '',
  isActiveInactive: '',
  description: '',
  destination: '',
  outputFilename: '',
  jobKind: '',
  params: '',
  expectedRunTime: {
    // 'weekly' | 'monthly'. Absent on every pre-existing doc, which reads as weekly.
    frequency: '',
    days: '', // weekly only
    monthDay: '', // monthly only: 1-31 | 'L' | 'LW'
    hrs: '',
    mins: '',
    secs: '',
  },
  lastRunTime: '',
  // Run outcome (read-only, written by the scheduler). lastRunTime alone cannot tell a
  // failed run from one that never fired — which is how a failed Forte upload read as
  // "the cron did not kick off".
  lastAttemptTime: '',
  lastRunStatus: '',
  lastError: '',
  lastErrorStage: '',
};

// The scheduler owns these; the client never sends them. cronJobInputType deliberately
// does not define them, so echoing a loaded grid row straight back into upsertCronJob
// fails GraphQL validation. Even if the input type accepted them, a save at 23:06 would
// overwrite the status of the run that had just failed at 23:05 with whatever the grid
// happened to be holding.
const RUN_STATUS_FIELDS = ['lastAttemptTime', 'lastRunStatus', 'lastError', 'lastErrorStage'];

const withoutRunStatus = (item) => {
  const payload = { ...item };
  RUN_STATUS_FIELDS.forEach((field) => { delete payload[field]; });
  return payload;
};

export const cronJobUtil = {
  namespaced: true,
  state: () => ({
    cronJobList: [],
    reportRoutes: [],
  }),
  mutations: {
    setCronJob(state, cronJobArr) {
      state.cronJobList = cronJobArr;
    },
    setReportRoutes(state, routes) {
      state.reportRoutes = routes;
    },
  },
  actions: {
    async getCronJobs({ commit, dispatch }, {
      criteria, fields, baseCrudKey,
    }) {
      let results;
      const gqlfields = fields || defaultFields;
      try {
        await dispatch('reAuth', null, { root: true });
        results = await backend.query({
          query: gql` query cronJobs($criteria: cronJobCriteriaType) {
            cronJobs(criteria: $criteria) {
                ${fieldsToGqlString(gqlfields)},
              }
            }`,
          variables: { criteria },
          fetchPolicy: 'no-cache',

        });
        clients.handleGQLErrors(results, commit);
        if (baseCrudKey && results.data && results.data.cronJobs) {
          const jobList = results.data ? results.data.cronJobs : [];
          commit('base/crud/setItems', [baseCrudKey, jobList], { root: true });
        }
        return results.data.cronJobs;
      } catch (e) {
        dispatch('logException', e, { root: true }); clients.handleGQLErrors(results, commit);
        dispatch('flashError', 'There was problem loading the jobs list!', { root: true });
        return [];
      }
    },
    async upsertCronJob({ dispatch, rootGetters, commit }, {
      item, fields, baseCrudKey,
    }) {
      let results;
      try {
        const gqlfields = fields || defaultFields;
        await dispatch('reAuth', null, { root: true });
        results = await backend.mutate({
          mutation: gql`mutation upsertCronJob($item: cronJobInputType!)  {
            upsertCronJob(upsert: $item) {
              ${fieldsToGqlString(gqlfields)},
            }
          }`,
          variables: {
            item: withoutRunStatus(item),
          },
        });
        clients.handleGQLErrors(results, commit);
        const cronJobStateData = rootGetters['base/crud/items'](baseCrudKey);
        const newCronJob = results.data.upsertCronJob;
        const newList = mergeByIdNewAtTop(cronJobStateData || [], newCronJob);
        commit('base/crud/setItems', [baseCrudKey, newList], { root: true });
      } catch (e) {
        dispatch('logException', e, { root: true }); clients.handleGQLErrors(results, commit);
        dispatch('flashError', 'There was a problem in upserting the Cron Job', { root: true });
      }
    },
    async getReportRoutes({ commit, dispatch }) {
      try {
        const res = await backendRest.get('/api/reportRoutes');
        commit('setReportRoutes', res.data || []);
        return res.data || [];
      } catch (e) {
        dispatch('logException', e, { root: true });
        return [];
      }
    },
    async getTrWorkFileProfiles({ dispatch }) {
      try {
        const res = await backendRest.get('/api/trWorkFile/profiles');
        return res.data || [];
      } catch (e) {
        dispatch('logException', e, { root: true });
        return [];
      }
    },
    async getTrWorkFileExportProfiles({ dispatch }) {
      try {
        const res = await backendRest.get('/api/trWorkFile/export-profiles');
        return res.data || [];
      } catch (e) {
        dispatch('logException', e, { root: true });
        return [];
      }
    },
    async deleteCronJob({ dispatch, rootGetters, commit }, { itemId, baseCrudKey }) {
      let results;
      try {
        await dispatch('reAuth', null, { root: true });
        results = await backend.mutate({
          mutation: gql`mutation deleteCronJob($_id: ID!) {
            deleteCronJob(_id: $_id) {
              _id
            }
          }`,
          variables: {
            _id: itemId,
          },
        });
        clients.handleGQLErrors(results, commit);
        const cronJobStateData = rootGetters['base/crud/items'](baseCrudKey);
        const newList = cronJobStateData.filter((item) => item._id !== itemId);
        commit('base/crud/setItems', [baseCrudKey, newList], { root: true });
      } catch (e) {
        dispatch('logException', e, { root: true }); clients.handleGQLErrors(results, commit);
        dispatch('flashError', 'There was a problem in deleting the Cron Job', { root: true });
      }
    },
  },
};

export default cronJobUtil;
